// TS mirrors of the API's pydantic output schemas
// (blended-learning-api: app/schemas/moodle.py, app/schemas/rag.py).
// Datetimes arrive as ISO strings.

export type RunStatus = "running" | "success" | "partial" | "failed" | string;

export interface MoodleCourse {
  moodle_id: number;
  shortname: string;
  fullname: string;
  summary: string | null;
  category_id: number | null;
  course_format: string | null;
  visible: boolean;
  moodle_timemodified: number | null;
  content_hash: string;
  synced_at: string | null;
  deleted_at: string | null;
  stats?: CourseStats; // on list, detail and course-sync responses
}

// `indexed` = approved and embedded at its current version, i.e. what the
// tutor can actually retrieve.
export interface CourseStats {
  modules: number;
  extraction_failed: number;
  documents: number;
  pending: number;
  approved: number;
  rejected: number;
  retired: number;
  indexed: number;
}

export interface MoodleSection {
  moodle_id: number;
  section_number: number;
  name: string | null;
  summary: string | null;
  visible: boolean;
  deleted_at: string | null;
}

export interface MoodleFile {
  filename: string;
  fileurl: string | null;
  mimetype: string | null;
  filesize: number | null;
  moodle_timemodified: number | null;
  deleted_at: string | null;
}

export interface MoodleModule {
  moodle_cmid: number;
  modname: string;
  name: string;
  description: string | null;
  url: string | null;
  visible: boolean;
  content_hash: string;
  extracted_hash: string | null;
  extraction_error: string | null; // set when the last extraction failed
  needs_extraction: boolean;
  synced_at: string | null;
  deleted_at: string | null;
  section_moodle_id: number | null;
  section_number: number | null;
  section_name: string | null;
  files: MoodleFile[];
}

// GET /moodle/modules: a module outside its course tree, so it names its course.
export interface MoodleModuleListItem extends MoodleModule {
  course_moodle_id: number | null;
  course_fullname: string | null;
}

export interface MoodleCourseDetail extends MoodleCourse {
  sections: MoodleSection[];
  modules: MoodleModule[];
}

export interface SyncRun {
  id: number;
  status: RunStatus;
  trigger: string;
  courses_seen: number;
  created_count: number;
  updated_count: number;
  deleted_count: number;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface ExtractionRun {
  id: number;
  status: RunStatus;
  trigger: string;
  modules_seen: number;
  documents_written: number;
  assets_written: number;
  bytes_downloaded: number;
  failures: number;
  failure_details: (ExtractionFailure | TruncatedFailures)[];
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

// failure_details is capped at 200 entries; past that the last entry is
// { truncated: n } (n more failures not listed). Older runs have [].
export interface TruncatedFailures {
  truncated: number;
}

export interface ExtractionFailure {
  cmid: number;
  module_name: string | null;
  error: string;
}

export interface IndexFailure {
  document_id: number;
  module_id: number;
  stage: "purge" | "index" | string;
  error: string;
}

export function isTruncated(entry: object): entry is TruncatedFailures {
  return "truncated" in entry;
}

export interface IndexRun {
  id: number;
  status: RunStatus;
  trigger: string;
  documents_seen: number;
  chunks_indexed: number;
  chunks_deleted: number;
  failures: number;
  failure_details: (IndexFailure | TruncatedFailures)[];
  error: string | null;
  started_at: string;
  finished_at: string | null;
}

export type AnyRun = SyncRun | ExtractionRun | IndexRun;

export interface SyncEvent {
  id: number;
  sync_run_id: number | null;
  entity_type: string; // course | section | module | file
  entity_moodle_id: number;
  course_moodle_id: number | null;
  action: string; // created | updated | deleted
  content_hash: string | null;
  payload: Record<string, unknown>;
  created_at: string | null;
}

export interface MoodleAsset {
  id: number;
  module_id: number;
  document_id: number | null;
  kind: string;
  source_path: string;
  filename: string;
  mimetype: string;
  filesize: number;
  sha256: string;
  alt_text: string | null;
  extracted_at: string | null;
  // Latest transcription row (audio/video only); null = never attempted.
  transcription_status: TranscriptionStatus | null;
  transcription_error: string | null;
  transcription_attempts: number | null;
  // Filled on GET /moodle/assets; null elsewhere.
  course_moodle_id: number | null;
  course_fullname: string | null;
  module_cmid: number | null;
  module_name: string | null;
}

// --- media transcription ---------------------------------------------------
// "pending" also covers media never attempted (transcription_status null).

export type TranscriptionStatus = "pending" | "processing" | "completed" | "failed" | "skipped";

export interface MediaTranscription {
  id: number;
  asset_id: number;
  status: TranscriptionStatus | string;
  model: string;
  model_version: string;
  language: string | null;
  duration_seconds: number | null;
  attempts: number;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
}

export interface MediaTranscriptionDetail extends MediaTranscription {
  text: string | null;
  segments: TranscriptSegment[];
}

export interface TranscriptDocument {
  id: number;
  title: string | null;
  approval_status: ApprovalStatus | string;
  media_start_seconds: number | null;
  media_end_seconds: number | null;
}

export interface AssetTranscription {
  transcription: MediaTranscriptionDetail;
  documents: TranscriptDocument[];
}

export type TranscriptionCounts = Record<TranscriptionStatus, number> & { total: number };

// Failed media grouped by identical error (GET /moodle/transcriptions/failures).
export interface TranscriptionFailureFile {
  id: number;
  filename: string;
  kind: string;
  filesize: number;
  attempts: number;
  course_moodle_id: number | null;
  course_fullname: string | null;
  module_cmid: number | null;
  module_name: string | null;
}

export interface TranscriptionFailureGroup {
  error: string;
  file_count: number;
  attempts: number; // combined across the group's files
  last_failed_at: string | null;
  files: TranscriptionFailureFile[];
}

export interface TranscriptionWatcherRun {
  trigger: "scheduled" | "manual" | string;
  started_at: string;
  finished_at: string | null;
  assets_seen?: number;
  completed?: number;
  failed?: number;
  error?: string;
}

export interface TranscriptionWatcher {
  enabled: boolean;
  paused: boolean;
  interval_minutes: number;
  last_check_at: string | null;
  next_check_at: string | null; // estimate: last scheduled check + interval
  last_run: TranscriptionWatcherRun | null;
  counts: TranscriptionCounts;
}

export interface MoodleDocument {
  id: number;
  course_id: number; // internal DB id, NOT the Moodle course id
  module_id: number; // internal DB id, NOT the Moodle cmid
  source_type: string;
  source_path: string;
  title: string | null;
  char_count: number;
  sort_order: number;
  content_hash: string;
  extracted_at: string | null;
  transcription_id: number | null;
  media_start_seconds: number | null;
  media_end_seconds: number | null;
}

export interface MoodleDocumentDetail extends MoodleDocument {
  text: string;
  assets: MoodleAsset[];
}

export type ApprovalStatus = "pending" | "approved" | "rejected" | "retired";

// Every approval/review response (pending, history, approve, reject, reopen,
// bulk) names its course and module by their Moodle ids; use those, not the
// internal course_id/module_id.
export interface ApprovalDocument extends MoodleDocument {
  approval_status: ApprovalStatus | string;
  reviewed_by_user_id: number | null;
  reviewed_at: string | null;
  review_note: string | null;
  course_moodle_id: number | null;
  course_fullname: string | null;
  module_cmid: number | null;
  module_name: string | null;
  section_name: string | null;
}

export interface ApprovalPage {
  documents: ApprovalDocument[];
  count: number; // this page
  total: number; // whole matching set
  limit: number;
  offset: number;
}

type StatusCounts = Record<ApprovalStatus, number>;

export interface ApprovalSummary {
  totals: StatusCounts;
  // Only courses that have documents.
  courses: (StatusCounts & { course_moodle_id: number; course_shortname: string; course_fullname: string })[];
}

// --- sync preview (dry run, nothing saved) -------------------------------------

export type PreviewStatus = "new" | "changed" | "unchanged" | "removed";

export interface ActionCountsOut {
  created: number;
  updated: number;
  deleted: number;
}

export interface SyncPreviewCourse extends ActionCountsOut {
  moodle_id: number;
  shortname: string;
  fullname: string;
  status: PreviewStatus;
  by_entity: Record<string, ActionCountsOut>;
  changes: Omit<SyncEvent, "id" | "sync_run_id" | "content_hash" | "created_at">[];
}

export interface SyncPreview {
  courses: SyncPreviewCourse[];
  totals: Record<PreviewStatus, number>; // "removed" only in a full preview
  missing: number[];
}

// --- system status ---------------------------------------------------------------

export type CheckStatus = "ok" | "error" | "disabled" | "not_configured";

export interface StatusCheck {
  status: CheckStatus | string;
  latency_ms: number | null;
  // A string explaining why whenever status isn't "ok"; otherwise null or a
  // check-specific object (workers, model, Moodle site info…).
  detail: string | Record<string, unknown> | null;
}

export interface RunSnapshot {
  id: number;
  status: RunStatus;
  started_at: string | null;
  finished_at: string | null;
}

export interface SystemStatus {
  status: "ok" | "degraded";
  checks: Record<string, StatusCheck>;
  pipeline: {
    latest_sync_run: RunSnapshot | null;
    latest_extraction_run: RunSnapshot | null;
    latest_index_run: RunSnapshot | null;
    extraction_backlog: number;
    index_backlog: number;
    pending_approval: number;
  } | null; // null when the database is down
}

// --- Celery tasks ------------------------------------------------------------------

export type TaskState = "PENDING" | "STARTED" | "SUCCESS" | "FAILURE" | "REVOKED";

export interface TaskStatus {
  task_id: string;
  state: TaskState | string;
  ready: boolean;
  successful: boolean;
  // On SUCCESS, the task summary; every pipeline task includes run_id.
  result: ({ run_id?: number; status?: string } & Record<string, unknown>) | null;
  error: string | null;
  date_done: string | null;
}

// Every long-running trigger (sync/extract/index/rebuild) either queues to
// Celery or, when no broker is reachable, runs inline and returns the run.
export type TriggerResult<R> =
  | { mode: "queued"; task_id: string }
  | { mode: "inline"; run: R };

export interface ContentGaps {
  recurring_refusals: { question: string; reason_code: string | null; count: number }[];
  low_rated_answers: {
    interaction_id: number;
    question: string;
    answer: string;
    average_rating: number;
    rating_count: number;
  }[];
}

// --- simulated patients (/sim-patient/admin/*) --------------------------------

export type ScenarioStatus = "draft" | "published" | "archived";
export type AttemptStatus = "in_progress" | "completed" | "abandoned";
export type Difficulty = "beginner" | "intermediate" | "advanced";

export interface HiddenHistoryItem {
  category: string;
  detail: string;
  trigger_hint?: string | null;
}

export interface RubricCriterion {
  criterion: string;
  max_score?: number | null;
  weight?: number | null;
  description?: string | null;
}

export interface DecisionPoint {
  id: number;
  sort_order: number;
  category: string;
  description: string;
  trigger_keywords: string[];
  reveals: string;
  max_score: number;
  is_critical: boolean;
  feedback_if_missed: string | null;
}

// Omitting `id` creates a new point; points left out of an update are deleted.
export type DecisionPointInput = Omit<DecisionPoint, "id" | "sort_order"> & { id?: number };

export interface ScenarioInput {
  title: string;
  presenting_complaint: string;
  course_moodle_id: number | null;
  module_cmid: number | null;
  difficulty: Difficulty;
  patient_persona: Record<string, string | number>;
  hidden_history: HiddenHistoryItem[];
  rubric: RubricCriterion[];
  decision_points: DecisionPointInput[];
}

export interface ScenarioListItem {
  id: number;
  title: string;
  difficulty: Difficulty;
  status: ScenarioStatus;
  version: number;
  total_max_score: number;
  course_moodle_id: number | null;
  course_name: string | null;
  module_cmid: number | null;
  module_name: string | null;
  authored_by_email: string | null;
  created_at: string;
  updated_at: string;
  decision_point_count: number;
  attempt_count: number;
  in_progress_count: number;
  completed_count: number;
  avg_score_pct: number | null;
  last_attempt_at: string | null;
}

export interface Scenario extends ScenarioListItem {
  presenting_complaint: string;
  patient_persona: Record<string, string | number>;
  hidden_history: HiddenHistoryItem[];
  rubric: RubricCriterion[];
  decision_points: DecisionPoint[];
  // Null for scenarios authored by hand before case templates existed.
  case_template: CaseTemplate | null;
}

// --- AI Simulated Patient Case Template (app/schemas/sim_case_template.py) ---

export type CaseDomain = "history_taking" | "examination" | "diagnosis" | "management" | "counseling" | "follow_up";

export interface ExpectedAction {
  id?: number | null; // decision point id once saved
  description: string;
  trigger_keywords: string[];
  reveals: string;
  points: number;
  is_critical: boolean;
  feedback_if_missed: string;
}

export interface CaseTemplate {
  case_information: {
    case_title: string;
    cpd_module: string;
    clinical_topic: string;
    difficulty: Difficulty;
    target_learners: string[];
    estimated_minutes: number;
    learning_objectives: string[];
  };
  reference_sources: {
    cpd_module: string;
    national_guideline: string;
    job_aid: string;
    sop_protocol: string;
    version_date: string;
  };
  patient_profile: {
    name: string;
    age: number | null;
    sex: string;
    occupation: string;
    residence: string;
    demeanor: string;
    speaking_style: string;
    chief_complaint: string;
    presenting_symptoms: string[];
    history_of_present_illness: string;
    past_medical_history: string;
    medication_history: string;
    allergies: string;
    obstetric_history: string;
    family_social_history: string;
    vital_signs: {
      temperature: string;
      blood_pressure: string;
      pulse: string;
      respiratory_rate: string;
      oxygen_saturation: string;
    };
    physical_examination: string[];
  };
  case_introduction: string;
  opening_statement: string;
  dialogue_steps: {
    patient_statement: string;
    options: { key: string; text: string; patient_response: string; score: number; feedback: string }[];
  }[];
  critical_decision_points: {
    domain: CaseDomain;
    expected_action: string;
    score: number;
    actions: ExpectedAction[];
  }[];
  performance_bands: { min_score: number; max_score: number; level: string; pathway: string }[];
  feedback_rules: string[];
  key_learning_points: string[];
  reflection_prompts: string[];
  metadata: {
    clinical_reviewer: string;
    technical_reviewer: string;
    approval_date: string;
    next_review_date: string;
  };
  // Approved course documents the case was generated from (set by the API).
  source_documents: {
    document_id: number;
    title: string | null;
    module_cmid: number | null;
    module_name: string | null;
    section_name: string | null;
  }[];
}

export type CaseSection = Exclude<keyof CaseTemplate, "metadata" | "source_documents">;

// PROGRESS meta the generate_sim_case worker task reports via GET /tasks/{id}.
export interface SimCaseProgress {
  kind: "generate" | "regenerate";
  stage: "sources" | "writing" | "repairing" | "saving";
  message: string;
  sections_done?: number;
  sections_total?: number;
  current_section?: string;
  characters?: number;
  passages?: number;
  documents?: number;
}

// Generation normally queues to the worker; without a broker it runs inline.
export type CaseJobResponse = { mode: "queued"; task_id: string } | { mode: "inline"; scenario: Scenario };

export interface GenerateScenarioInput {
  course_moodle_id: number;
  module_cmid: number | null;
  difficulty: Difficulty | null; // null lets the model choose
  target_learners: string[];
  focus: string | null;
}

export interface ScenarioPage {
  scenarios: ScenarioListItem[];
  count: number;
  total: number;
  offset: number;
  limit: number;
}

export interface AttemptListItem {
  id: number;
  session_id: number;
  scenario_id: number;
  scenario_title: string;
  user_id: number;
  user_email: string | null;
  user_full_name: string | null;
  status: AttemptStatus;
  total_score: number | null;
  max_possible_score: number;
  score_pct: number | null;
  turn_count: number;
  started_at: string;
  finished_at: string | null;
}

export interface AttemptPage {
  attempts: AttemptListItem[];
  count: number;
  total: number;
  offset: number;
  limit: number;
}

export interface AttemptDetail extends AttemptListItem {
  performance_level: string | null; // from the case template's bands
  events: {
    id: number;
    event_type: string;
    decision_point_id: number | null;
    decision_point_description: string | null;
    student_input: string;
    patient_response: string;
    score_awarded: number;
    created_at: string;
  }[];
  rubric_scores: { criterion: string; score: number; max_score: number; feedback: string | null }[];
  messages: { id: number; role: string; content: string; created_at: string }[];
  decision_points: {
    id: number;
    category: string;
    description: string;
    max_score: number;
    is_critical: boolean;
    hit: boolean;
    score_awarded: number;
  }[];
}

export interface DailyCount {
  date: string;
  started: number;
  completed: number;
}

export interface AttemptTotals {
  started: number;
  in_progress: number;
  completed: number;
  abandoned: number;
  completion_rate: number | null;
  avg_score_pct: number | null;
  unique_learners: number;
}

export interface SimSummary {
  scenarios_by_status: Record<ScenarioStatus, number>;
  attempts: AttemptTotals;
  daily: DailyCount[];
  scenarios: ScenarioListItem[];
}

export interface ScenarioReport {
  scenario_id: number;
  attempts: AttemptTotals;
  score_distribution: { label: string; count: number }[];
  decision_points: {
    id: number;
    sort_order: number;
    category: string;
    description: string;
    is_critical: boolean;
    max_score: number;
    hit_count: number;
    hit_rate: number | null;
  }[];
  rubric: { criterion: string; avg_score: number; max_score: number; avg_pct: number | null; samples: number }[];
  critical_miss_rate: number | null;
  daily: DailyCount[];
}
